import { LOGOS_IMPRESOS } from '../../config'
import { Bien, Recinto } from '../../types/inventario'
import { ESTADO_FISICO, ordenarParaPlancheta, SECTOR } from '../../utils/inventario'

/**
 * Filas de bienes por hoja. Cada celda muestra a lo más dos líneas (styles/app.css), así
 * que una hoja nunca se desborda a la siguiente: la paginación es de la app, no del
 * navegador.
 */
export const FILAS_POR_HOJA = 20
/** Anchos de las columnas B–H de la plantilla Excel de la FOR-OAD-002 (en %). */
const ANCHOS = [3.9, 8.4, 27.9, 14.1, 19, 14.6, 12.1]
const FECHA = new Intl.DateTimeFormat('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Santiago' })

/** 'OF. 310 (ANAKENA)' en el Norte, piso 3 → 'OFICINA 310 (ANAKENA) · EDIFICIO NORTE · PISO 3'. */
function ubicacionPlancheta(r: Recinto) {
  const partes = [r.nombre.replace(/^OF\. /, 'OFICINA '), `EDIFICIO ${SECTOR[r.sector].toUpperCase()}`]
  if (r.piso != null) partes.push(`PISO ${r.piso}`)
  return partes.join(' · ')
}

/** «MALO · PATA SUELTA»: el estado físico (si está evaluado) y la observación, en una línea. */
function observacionPlancheta(b: Bien) {
  const fisico = b.estado_fisico ? ESTADO_FISICO[b.estado_fisico].texto.toUpperCase() : ''
  return [fisico, b.observacion.replace(/\s+/g, ' ').trim()].filter(Boolean).join(' · ')
}

/** Una celda con a lo más dos líneas (el resto se corta): mantiene fijo el alto de la fila. */
function Celda({ children, className }: { children?: string; className?: string }) {
  return (
    <td className={className}>
      <div className="pl-celda">{children}</div>
    </td>
  )
}

/**
 * La plancheta de bienes (FOR-OAD-002) como el formulario en papel. Cada hoja es el
 * formulario completo: logos, fecha, unidad, ubicación con «N de M páginas», hasta 20
 * bienes (numerados de corrido entre hojas), las dos firmas y observaciones. Así cada
 * hoja se firma y vale por sí sola. En la columna OBSERVACIÓN va la observación del
 * bien, precedida por su estado físico si está evaluado (lo que el procedimiento pide
 * registrar). Es una pieza de marca: se imprime siempre
 * en blanco y negro, también en tema oscuro.
 */
export function PlanchetaDoc({ recinto, bienes, fecha }: { recinto: Recinto; bienes: Bien[]; fecha: Date }) {
  const filas = ordenarParaPlancheta(bienes)
  const hojas = Math.max(1, Math.ceil(filas.length / FILAS_POR_HOJA))
  return (
    <div className="plancheta-hojas">
      {Array.from({ length: hojas }, (_, h) => (
        <HojaPlancheta
          key={h}
          recinto={recinto}
          fecha={fecha}
          hoja={h + 1}
          hojas={hojas}
          desde={h * FILAS_POR_HOJA}
          bienes={filas.slice(h * FILAS_POR_HOJA, (h + 1) * FILAS_POR_HOJA)}
        />
      ))}
    </div>
  )
}

function HojaPlancheta({
  recinto, fecha, hoja, hojas, desde, bienes,
}: {
  recinto: Recinto
  fecha: Date
  hoja: number
  hojas: number
  desde: number
  bienes: Bien[]
}) {
  const vacias = FILAS_POR_HOJA - bienes.length
  const columnas = (
    <colgroup>
      {ANCHOS.map((ancho, i) => (
        <col key={i} style={{ width: `${ancho}%` }} />
      ))}
    </colgroup>
  )
  return (
    <article className="plancheta-doc pl-hoja" aria-label={`Plancheta de ${recinto.nombre}, hoja ${hoja} de ${hojas}`}>
      <div className="pl-logos">
        <img src={LOGOS_IMPRESOS.fcfm} alt="Facultad de Ciencias Físicas y Matemáticas, Universidad de Chile" />
        <img src={LOGOS_IMPRESOS.dcc} alt="Departamento de Ciencias de la Computación, Universidad de Chile" />
      </div>
      <div className="pl-marco">
        <div className="pl-fecha">
          <b>FECHA</b>
          <span className="pl-caja">{FECHA.format(fecha)}</span>
        </div>
        <div className="pl-titulo">PLANCHETA DE BIENES (FOR-OAD-002)</div>
        <table className="pl-tabla pl-datos">
          {columnas}
          <tbody>
            <tr>
              <th colSpan={3}>UNIDAD ACADÉMICA</th>
              <td colSpan={4}>DEPARTAMENTO DE CIENCIAS DE LA COMPUTACIÓN</td>
            </tr>
            <tr>
              <th colSpan={3}>IDENTIFICACIÓN DE LA UBICACIÓN</th>
              <td colSpan={4}>
                <div className="pl-ubicacion">
                  <span>{ubicacionPlancheta(recinto)}</span>
                  <span className="pl-pagina">
                    {hoja} de {hojas} {hojas === 1 ? 'página' : 'páginas'}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <table className="pl-tabla pl-bienes">
          {columnas}
          <thead>
            <tr>
              <th>N°</th>
              <th>N° BIEN</th>
              <th>DESCRIPCIÓN DEL BIEN</th>
              <th>MARCA</th>
              <th>MODELO</th>
              <th>N° SERIE</th>
              <th>OBSERVACIÓN</th>
            </tr>
          </thead>
          <tbody>
            {bienes.map((b, i) => (
              <tr key={b.id}>
                <th>{desde + i + 1}</th>
                <Celda>{b.numero}</Celda>
                <Celda>{b.descripcion}</Celda>
                <Celda>{b.marca}</Celda>
                <Celda>{b.modelo === 'S/M' ? '' : b.modelo}</Celda>
                <Celda>{b.serie}</Celda>
                <Celda className="pl-obs">{observacionPlancheta(b)}</Celda>
              </tr>
            ))}
            {Array.from({ length: vacias }, (_, k) => (
              <tr key={`vacia-${k}`}>
                <th>{desde + bienes.length + k + 1}</th>
                <Celda /><Celda /><Celda /><Celda /><Celda /><Celda />
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pl-firmas">
          <div className="pl-firma pl-firma--oficina">
            {recinto.encargado && <span className="pl-firma__nombre">{recinto.encargado.nombre.toUpperCase()}</span>}
            NOMBRE Y FIRMA
            <br />
            ENCARGADO DE OFICINA
          </div>
          <div className="pl-firma pl-firma--inventario">
            NOMBRE Y FIRMA
            <br />
            ENCARGADO DE INVENTARIO DCC
          </div>
        </div>
        <div className="pl-observaciones">
          <b>OBSERVACIONES:</b>
          <span />
        </div>
      </div>
    </article>
  )
}
